import { useState } from "react";
import "@/App.css";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    ArrowRight,
    ArrowDown,
    Check,
    Globe2,
    MapPin,
    Sparkles,
    Menu,
    X,
    Download,
} from "lucide-react";

const LEAD_MAGNET_URL =
    "https://drive.google.com/file/d/1AvJvp7UyYGCZwjOyY2LYvDh-nAGJDAVS/view";

const BOOKING_URL =
    "https://calendly.com/rochelle-fastreply247/free-ai-discovery-call";

const Diamond = ({ className = "" }) => (
    <svg
        viewBox="0 0 64 120"
        className={className}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M32 2 L60 60 L32 118 L4 60 Z"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
        />
        <circle cx="32" cy="60" r="6" fill="currentColor" />
    </svg>
);

const GoldBullet = () => (
    <span
        aria-hidden="true"
        className="inline-block text-[color:var(--wa-gold)] mr-3 translate-y-[1px]"
    >
        ✦
    </span>
);

// ---------- Nav ----------
const Nav = () => {
    const [open, setOpen] = useState(false);
    return (
        <header
            className="sticky top-0 z-50 wa-section-teal border-b border-[rgba(201,169,110,0.18)]"
            data-testid="site-nav"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <a
                    href="#top"
                    className="flex items-center gap-3"
                    data-testid="nav-logo"
                >
                    <img
                        src="/wa_horizontal.png"
                        alt="Weber Advisory"
                        className="h-10 sm:h-11 w-auto object-contain"
                    />
                </a>

                <nav
                    className="hidden md:flex items-center gap-9"
                    aria-label="Primary"
                >
                    <a
                        href="#top"
                        className="wa-nav-link"
                        data-testid="nav-home"
                    >
                        Home
                    </a>
                    <a
                        href="#services"
                        className="wa-nav-link"
                        data-testid="nav-services"
                    >
                        Services
                    </a>
                    <a
                        href="#about"
                        className="wa-nav-link"
                        data-testid="nav-about"
                    >
                        About
                    </a>
                    <a
                        href="#faq"
                        className="wa-nav-link"
                        data-testid="nav-faq"
                    >
                        FAQ
                    </a>
                </nav>

                <div className="hidden md:block">
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-btn-primary"
                        data-testid="nav-book-cta"
                    >
                        Book a Free Discovery Call
                    </a>
                </div>

                <button
                    type="button"
                    className="md:hidden text-[color:var(--wa-warm-white)] p-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="md:hidden border-t border-[rgba(201,169,110,0.2)] bg-[color:var(--wa-teal-dark)]"
                    data-testid="nav-mobile-panel"
                >
                    <div className="px-5 py-5 flex flex-col gap-4">
                        <a
                            href="#top"
                            onClick={() => setOpen(false)}
                            className="wa-nav-link"
                        >
                            Home
                        </a>
                        <a
                            href="#services"
                            onClick={() => setOpen(false)}
                            className="wa-nav-link"
                        >
                            Services
                        </a>
                        <a
                            href="#about"
                            onClick={() => setOpen(false)}
                            className="wa-nav-link"
                        >
                            About
                        </a>
                        <a
                            href="#faq"
                            onClick={() => setOpen(false)}
                            className="wa-nav-link"
                        >
                            FAQ
                        </a>
                        <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wa-btn-primary w-full"
                            data-testid="nav-mobile-book-cta"
                        >
                            Book a Free Discovery Call
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

// ---------- Hero ----------
const Hero = () => (
    <section
        id="top"
        className="wa-hero-bg relative overflow-hidden"
        data-testid="hero-section"
    >
        <div className="wa-grain" />
        <Diamond className="wa-diamond-watermark w-[180px] h-[320px] -left-10 top-16 hidden md:block" />
        <Diamond className="wa-diamond-watermark w-[220px] h-[400px] -right-16 bottom-0 hidden lg:block" />

        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(201,169,110,0.35)] text-[color:var(--wa-gold)] text-xs tracking-[0.22em] uppercase font-semibold mb-8 wa-fade-up">
                <Sparkles size={14} />
                AI Adoption Consulting — Australia
            </div>

            <p
                className="font-serif-brand text-[color:var(--wa-gold)] text-xl sm:text-2xl lg:text-3xl uppercase tracking-[0.28em] font-light mb-6 sm:mb-8 wa-fade-up"
                data-testid="hero-tagline"
            >
                Where AI gets to work
            </p>

            <h1
                className="font-serif-brand text-[color:var(--wa-warm-white)] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] wa-fade-up"
                style={{ animationDelay: "80ms" }}
                data-testid="hero-headline"
            >
                AI isn’t just for big companies.
                <br />
                <span className="italic text-[color:var(--wa-gold-soft)]">
                    Let’s build it into yours.
                </span>
            </h1>

            <p
                className="mt-6 max-w-2xl mx-auto text-[color:var(--wa-gold)] text-base sm:text-lg font-medium wa-fade-up"
                style={{ animationDelay: "160ms" }}
                data-testid="hero-subheadline"
            >
                Practical AI adoption consulting for Australian small businesses
                — without the jargon, the overwhelm, or the big agency price
                tag.
            </p>

            <p
                className="mt-5 max-w-2xl mx-auto text-[color:rgba(247,243,236,0.78)] text-sm sm:text-base leading-relaxed wa-fade-up"
                style={{ animationDelay: "220ms" }}
            >
                Book a free 30-minute discovery call and find out exactly where
                AI can save your business time and money — this week.
            </p>

            <div
                className="mt-10 flex flex-col items-center gap-4 wa-fade-up"
                style={{ animationDelay: "300ms" }}
            >
                <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wa-btn-primary large"
                    data-testid="hero-primary-cta"
                >
                    Book Your Free Discovery Call <ArrowRight size={18} />
                </a>
                <a
                    href="#how-it-works"
                    className="wa-underline-link inline-flex items-center gap-1.5"
                    data-testid="hero-see-how"
                >
                    See how it works <ArrowDown size={14} />
                </a>
            </div>

            <div
                className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[color:rgba(201,169,110,0.85)] text-xs sm:text-sm wa-fade-up"
                style={{ animationDelay: "380ms" }}
                data-testid="hero-trust-bar"
            >
                <span className="inline-flex items-center gap-2">
                    <Globe2 size={14} /> 100% Remote — Zoom or Google Meet
                </span>
                <span className="hidden sm:inline text-[color:rgba(201,169,110,0.4)]">
                    ✦
                </span>
                <span className="inline-flex items-center gap-2">
                    <MapPin size={14} /> Australian Business
                </span>
                <span className="hidden sm:inline text-[color:rgba(201,169,110,0.4)]">
                    ✦
                </span>
                <span className="inline-flex items-center gap-2">
                    <Check size={14} /> No Tech Background Required
                </span>
            </div>
        </div>
    </section>
);

// ---------- Problem / Stats ----------
const Problem = () => {
    const stats = [
        {
            stat: "1 in 3",
            desc: "Australian SMBs say they don’t know where to start with AI",
            source: "Deloitte / Amazon 2025",
        },
        {
            stat: "88%",
            desc: "of small business owners want AI training and hands-on support",
            source: "Goldman Sachs 2025",
        },
        {
            stat: "+45–111%",
            desc: "profitability gain when SMBs move up just one AI maturity step",
            source: "Australian Government AI Tracker",
        },
    ];
    return (
        <section
            id="problem"
            className="bg-[color:var(--wa-warm-white)] py-20 sm:py-28"
            data-testid="problem-section"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
                <div className="eyebrow">The Opportunity You’re Missing</div>
                <h2
                    className="mt-5 font-serif-brand text-[color:var(--wa-teal)] text-3xl sm:text-4xl lg:text-5xl leading-[1.12] max-w-3xl mx-auto"
                    data-testid="problem-headline"
                >
                    Most small businesses know AI is important.
                    <br />
                    <span className="italic text-[color:var(--wa-teal-dark)]">
                        Almost none know where to start.
                    </span>
                </h2>
                <hr className="wa-divider mx-auto mt-8" />

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-7 text-left">
                    {stats.map((s, i) => (
                        <div
                            key={i}
                            className="wa-stat-card rounded-xl p-8"
                            data-testid={`stat-card-${i + 1}`}
                        >
                            <div className="wa-stat-number text-5xl sm:text-[3.25rem] leading-none">
                                {s.stat}
                            </div>
                            <p className="mt-5 text-[color:var(--wa-warm-white)] text-base leading-relaxed">
                                {s.desc}
                            </p>
                            <p className="mt-5 text-[color:rgba(201,169,110,0.85)] text-xs tracking-widest uppercase">
                                Source: {s.source}
                            </p>
                        </div>
                    ))}
                </div>

                <p
                    className="mt-14 max-w-3xl mx-auto text-[color:var(--wa-ink)] text-base sm:text-lg leading-relaxed"
                    data-testid="problem-closing"
                >
                    The tools exist. The gap isn’t technology — it’s knowing{" "}
                    <em>which</em> tools to use, how to set them up, and in what
                    order. That’s exactly what Weber Advisory does.
                </p>
            </div>
        </section>
    );
};

// ---------- How It Works ----------
const HowItWorks = () => {
    const steps = [
        {
            n: "1",
            title: "Book your free discovery call",
            body: "A relaxed 30-minute conversation about your business, your biggest time drains, and where you want to be in 6 months. No sales pitch, no jargon.",
        },
        {
            n: "2",
            title: "Complete the AI Readiness Questionnaire",
            body: "A short pre-session form so we make the most of our time together. Takes about 10 minutes.",
        },
        {
            n: "3",
            title: "Your 90-Minute AI Audit + Written Report",
            body: "A deep-dive session via Zoom where we map every area of your business against 7 AI opportunity zones. You walk away with a written AI Opportunities Report and a prioritised action plan.",
        },
    ];
    return (
        <section
            id="how-it-works"
            className="wa-section-teal relative overflow-hidden py-20 sm:py-28"
            data-testid="how-it-works-section"
        >
            <Diamond className="wa-diamond-watermark w-[200px] h-[360px] -right-10 top-20 hidden md:block" />
            <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
                <div className="text-center">
                    <div className="eyebrow">How it works</div>
                    <h2
                        className="mt-5 font-serif-brand text-[color:var(--wa-warm-white)] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] max-w-3xl mx-auto"
                        data-testid="how-it-works-headline"
                    >
                        From “where do I start” to a clear AI roadmap —{" "}
                        <span className="italic text-[color:var(--wa-gold-soft)]">
                            in three steps.
                        </span>
                    </h2>
                    <hr className="wa-divider mx-auto mt-8" />
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
                    {steps.map((s, i) => (
                        <div
                            key={s.n}
                            className="text-left"
                            data-testid={`step-${s.n}`}
                        >
                            <div className="wa-step-circle mb-6">{s.n}</div>
                            <h3 className="font-serif-brand text-[color:var(--wa-warm-white)] text-xl sm:text-2xl leading-snug">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-[color:rgba(247,243,236,0.78)] text-[15px] leading-relaxed">
                                {s.body}
                            </p>
                            {i < steps.length - 1 && (
                                <div className="hidden md:block mt-6 h-px bg-[rgba(201,169,110,0.2)]" />
                            )}
                        </div>
                    ))}
                </div>

                <div className="mt-14 text-center">
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-btn-primary large"
                        data-testid="how-it-works-cta"
                    >
                        Book Your Free Call <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </section>
    );
};

// ---------- Lead Magnet ----------
const LeadMagnet = () => (
    <section
        className="bg-[color:var(--wa-teal)] border-y border-[rgba(201,169,110,0.18)] py-12 sm:py-14"
        data-testid="lead-magnet-section"
    >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-7 md:gap-10">
            <div className="text-center md:text-left max-w-2xl">
                <h3
                    className="font-serif-brand text-[color:var(--wa-warm-white)] text-2xl sm:text-[1.7rem] leading-snug"
                    data-testid="lead-magnet-headline"
                >
                    Not sure if you’re ready for AI?{" "}
                    <span className="italic text-[color:var(--wa-gold-soft)]">
                        Start here.
                    </span>
                </h3>
                <p className="mt-3 text-[color:rgba(247,243,236,0.82)] text-[15px] sm:text-base leading-relaxed">
                    Download the free SME AI Readiness Checklist — a
                    plain-English guide to the 7 areas of your business where
                    AI can save you time and money right now.
                </p>
            </div>
            <a
                href={LEAD_MAGNET_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="wa-btn-primary shrink-0"
                data-testid="lead-magnet-cta"
            >
                <Download size={18} /> Download the Free Checklist
            </a>
        </div>
    </section>
);

// ---------- Services ----------
const Services = () => {
    const cards = [
        {
            name: "AI Readiness Audit",
            highlight: true,
            price: "From $497 AUD",
            best: "Business owners who want clarity before committing to implementation",
            includes: [
                "Pre-audit questionnaire",
                "90-min AI Audit (Zoom)",
                "Written AI Opportunities Report (5–10 pages)",
                "Prioritised Quick Wins list",
                "Tool recommendations with cost estimates",
            ],
            cta: "Book an Audit",
            badge: "Most Popular",
        },
        {
            name: "Audit + Coaching Package",
            price: "From $1,497 AUD",
            best: "Business owners ready to take action with expert guidance",
            includes: [
                "Everything in the Audit",
                "3 × 60-min coaching sessions (fortnightly)",
                "Session recordings + action notes",
                "Email support between sessions",
            ],
            cta: "Enquire Now",
        },
        {
            name: "Done-For-You Setup",
            price: "$2,500–$5,000 AUD",
            best: "Owners who want results without doing it themselves",
            includes: [
                "Full AI Audit",
                "Full implementation of agreed tools and automations",
                "Staff training session (60 min)",
                "30-day post-setup support",
            ],
            cta: "Enquire Now",
        },
        {
            name: "Monthly AI Advisor Retainer",
            price: "From $497/month AUD",
            best: "Businesses that want AI kept up to date as they grow",
            includes: [
                "2 × 45-min calls per month",
                "Unlimited email/message support",
                "New tool recommendations as AI evolves",
            ],
            cta: "Enquire Now",
            tag: "Ongoing",
        },
    ];

    return (
        <section
            id="services"
            className="bg-[color:var(--wa-warm-white)] py-20 sm:py-28"
            data-testid="services-section"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="text-center">
                    <div className="eyebrow">Services</div>
                    <h2
                        className="mt-5 font-serif-brand text-[color:var(--wa-teal)] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] max-w-3xl mx-auto"
                        data-testid="services-headline"
                    >
                        Choose the level of support that suits your{" "}
                        <span className="italic">business.</span>
                    </h2>
                    <hr className="wa-divider mx-auto mt-8" />
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {cards.map((c, i) => {
                        const base =
                            "relative rounded-xl p-7 sm:p-8 flex flex-col h-full";
                        const theme = c.highlight
                            ? "wa-card-highlight"
                            : "wa-card-standard";
                        return (
                            <div
                                key={c.name}
                                className={`${base} ${theme}`}
                                data-testid={`service-card-${i + 1}`}
                            >
                                {c.badge && (
                                    <span
                                        className="wa-badge absolute -top-3 left-6"
                                        data-testid="service-badge-popular"
                                    >
                                        {c.badge}
                                    </span>
                                )}
                                {c.tag && (
                                    <span className="wa-badge-muted absolute -top-3 left-6 text-[0.65rem]">
                                        {c.tag}
                                    </span>
                                )}

                                <h3
                                    className={`font-serif-brand text-xl sm:text-[1.4rem] leading-snug ${c.highlight ? "text-[color:var(--wa-warm-white)]" : "text-[color:var(--wa-teal)]"}`}
                                >
                                    {c.name}
                                </h3>
                                <div
                                    className={`mt-2 font-semibold ${c.highlight ? "text-[color:var(--wa-gold)]" : "text-[color:var(--wa-teal-dark)]"}`}
                                >
                                    {c.price}
                                </div>

                                <p
                                    className={`mt-4 text-sm leading-relaxed ${c.highlight ? "text-[color:rgba(247,243,236,0.78)]" : "text-[color:var(--wa-ink-muted)]"}`}
                                >
                                    <span className="font-semibold">
                                        Best for:
                                    </span>{" "}
                                    {c.best}
                                </p>

                                <ul
                                    className={`mt-5 space-y-2.5 text-sm ${c.highlight ? "text-[color:rgba(247,243,236,0.9)]" : "text-[color:var(--wa-ink)]"}`}
                                >
                                    {c.includes.map((item, k) => (
                                        <li
                                            key={k}
                                            className="flex gap-2.5 items-start"
                                        >
                                            <Check
                                                size={16}
                                                className="mt-0.5 shrink-0 text-[color:var(--wa-gold)]"
                                            />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="flex-1" />

                                <a
                                    href={BOOKING_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`mt-7 ${c.highlight ? "wa-btn-primary" : "wa-btn-outline"} w-full justify-center`}
                                    data-testid={`service-cta-${i + 1}`}
                                >
                                    {c.cta} <ArrowRight size={16} />
                                </a>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-sm text-[color:var(--wa-ink-muted)]">
                    Not sure which is right for you?{" "}
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[color:var(--wa-teal)] underline underline-offset-4 decoration-[color:var(--wa-gold)] hover:text-[color:var(--wa-teal-dark)]"
                    >
                        Book a free 30-min discovery call
                    </a>{" "}
                    and we’ll figure it out together.
                </p>
            </div>
        </section>
    );
};

// ---------- About ----------
const About = () => (
    <section
        id="about"
        className="bg-[color:var(--wa-warm-white)] pb-20 sm:pb-28 pt-4"
        data-testid="about-section"
    >
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                {/* Photo placeholder */}
                <div className="lg:col-span-5">
                    <div
                        className="wa-photo-placeholder aspect-[4/5] w-full"
                        data-testid="about-photo-placeholder"
                    />
                </div>

                {/* Text */}
                <div className="lg:col-span-7">
                    <div className="eyebrow">About</div>
                    <h2
                        className="mt-5 font-serif-brand text-[color:var(--wa-teal)] text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.15]"
                        data-testid="about-headline"
                    >
                        I didn’t study AI.{" "}
                        <span className="italic">
                            I built five businesses with it.
                        </span>
                    </h2>
                    <hr className="wa-divider mt-7" />

                    <div className="mt-7 space-y-5 text-[color:var(--wa-ink)] text-[15.5px] sm:text-base leading-relaxed">
                        <p>
                            Hi, I’m <strong>Rochelle Weber</strong> — a business
                            owner, builder, and AI adoption practitioner based
                            in Australia.
                        </p>
                        <p>
                            Over the past few years I’ve built and launched five
                            AI-powered products from scratch — including an
                            automated SMS reply platform, a social media reel
                            creator, and a lead generation tool — all without a
                            tech background or a development team.
                        </p>
                        <p>
                            I didn’t learn AI from a textbook. I learned it by
                            doing it, making mistakes, figuring out what
                            actually works for real businesses, and doing it
                            again.
                        </p>
                        <p>
                            Weber Advisory exists because I built AI into my
                            own businesses — and figured out what actually
                            works, what wastes money, and where the real wins
                            hide. That knowledge is what I bring to every
                            client.
                        </p>
                        <p>
                            This isn’t consulting theory. It’s practical,
                            plain-English AI adoption from someone who’s in it
                            every day.
                        </p>
                    </div>

                    <ul className="mt-8 space-y-3 text-[color:var(--wa-ink)]">
                        <li className="flex items-start">
                            <GoldBullet />
                            <span>Founded and launched 5 AI-powered products</span>
                        </li>
                        <li className="flex items-start">
                            <GoldBullet />
                            <span>
                                Featured on the SureThing AI Business Podcast
                            </span>
                        </li>
                        <li className="flex items-start">
                            <GoldBullet />
                            <span>
                                100% remote — working with Australian SMBs
                                nationally
                            </span>
                        </li>
                        <li className="flex items-start">
                            <GoldBullet />
                            <span>No jargon, no fluff, no tech-speak</span>
                        </li>
                    </ul>

                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-btn-primary mt-9"
                        data-testid="about-cta"
                    >
                        Work With Me <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </div>
    </section>
);

// ---------- FAQ ----------
const FAQ = () => {
    const faqs = [
        {
            q: "Do I need to be tech-savvy for this to work?",
            a: "Not at all. My clients are business owners — not developers. Everything is explained in plain English and I always recommend tools that are genuinely easy to use, not just impressive on paper.",
        },
        {
            q: "What kinds of businesses do you work with?",
            a: "Primarily Australian small to medium businesses — tradies, service businesses, retail, professional services, and online businesses. If you’re run off your feet doing tasks a computer could handle, we should talk.",
        },
        {
            q: "What happens in the free discovery call?",
            a: "It’s a relaxed 30-minute conversation — no sales pitch, no pressure. We talk about your business, where you’re spending the most time, and whether an AI audit makes sense for you. If it’s not a fit, I’ll tell you honestly.",
        },
        {
            q: "How is this different from a big consulting firm?",
            a: "I’m not selling you a strategy deck and flying out. I’m a working business owner who uses these tools every day. I know what’s realistic for a small business budget and what actually delivers results — not what looks good in a proposal.",
        },
        {
            q: "What do I get from the AI Opportunities Report?",
            a: "A 5–10 page written document that covers your current workflows, where AI can help (with specific tool recommendations and cost estimates), a prioritised Quick Wins list, and suggested next steps. It’s yours to keep and act on — with or without me.",
        },
        {
            q: "How quickly can I get started?",
            a: "Discovery calls are typically available within a few days. After the audit session, your written report is delivered within 5 business days.",
        },
    ];
    return (
        <section
            id="faq"
            className="wa-section-teal relative overflow-hidden py-20 sm:py-28"
            data-testid="faq-section"
        >
            <Diamond className="wa-diamond-watermark w-[180px] h-[320px] -left-14 top-20 hidden md:block" />
            <div className="max-w-3xl mx-auto px-5 sm:px-8 relative">
                <div className="text-center">
                    <div className="eyebrow">FAQ</div>
                    <h2
                        className="mt-5 font-serif-brand text-[color:var(--wa-warm-white)] text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.15]"
                        data-testid="faq-headline"
                    >
                        Questions you’re probably{" "}
                        <span className="italic text-[color:var(--wa-gold-soft)]">
                            thinking about.
                        </span>
                    </h2>
                    <hr className="wa-divider mx-auto mt-7" />
                </div>

                <Accordion
                    type="single"
                    collapsible
                    className="wa-faq mt-12 divide-y divide-[rgba(201,169,110,0.18)] border-y border-[rgba(201,169,110,0.18)]"
                    data-testid="faq-accordion"
                >
                    {faqs.map((item, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            className="border-0"
                        >
                            <AccordionTrigger
                                className="wa-faq-trigger text-left font-serif-brand text-lg sm:text-xl text-[color:var(--wa-warm-white)] hover:text-[color:var(--wa-gold)] hover:no-underline py-6 [&>svg]:text-[color:var(--wa-gold)] [&>svg]:w-5 [&>svg]:h-5"
                                data-testid={`faq-trigger-${i + 1}`}
                            >
                                {item.q}
                            </AccordionTrigger>
                            <AccordionContent
                                className="text-[color:rgba(247,243,236,0.82)] text-[15px] leading-relaxed pb-6 pr-6"
                                data-testid={`faq-content-${i + 1}`}
                            >
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
};

// ---------- Final CTA + Footer ----------
const FinalCTAAndFooter = () => (
    <>
        <section
            className="wa-section-teal relative overflow-hidden py-24 sm:py-32 border-t border-[rgba(201,169,110,0.18)]"
            data-testid="final-cta-section"
        >
            <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
                <h2
                    className="font-serif-brand text-[color:var(--wa-warm-white)] text-3xl sm:text-4xl lg:text-5xl leading-[1.15]"
                    data-testid="final-cta-headline"
                >
                    Ready to find out what AI could do for{" "}
                    <span className="italic text-[color:var(--wa-gold-soft)]">
                        your business?
                    </span>
                </h2>
                <p className="mt-5 text-[color:var(--wa-gold)] text-base sm:text-lg">
                    Book your free 30-minute discovery call. No commitment, no
                    jargon — just clarity.
                </p>
                <div className="mt-10">
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-btn-primary large"
                        data-testid="final-cta-button"
                    >
                        Book Your Free Discovery Call <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </section>

        <footer
            className="bg-[color:var(--wa-teal-deep)] py-10"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-[color:rgba(247,243,236,0.6)] text-xs sm:text-sm">
                <div className="flex items-center gap-3">
                    <img
                        src="/wa_icon.png"
                        alt="Weber Advisory"
                        className="h-8 w-8 rounded-md"
                    />
                    <span>
                        © 2026 Weber Advisory |{" "}
                        <a
                            href="mailto:rochelle@weberadvisory.com.au"
                            className="hover:text-[color:var(--wa-gold)] transition-colors"
                            data-testid="footer-email"
                        >
                            rochelle@weberadvisory.com.au
                        </a>
                    </span>
                </div>
                <div className="text-center sm:text-right">
                    All sessions delivered via Zoom or Google Meet | Australia-wide
                </div>
            </div>
        </footer>
    </>
);

function App() {
    return (
        <div className="App bg-[color:var(--wa-warm-white)]" data-testid="app-root">
            <Nav />
            <main>
                <Hero />
                <Problem />
                <HowItWorks />
                <LeadMagnet />
                <Services />
                <About />
                <FAQ />
                <FinalCTAAndFooter />
            </main>
        </div>
    );
}

export default App;
